import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import Swal from 'sweetalert2';
import { Router } from '@angular/router';

import { forkJoin, Observable, throwError, of } from "rxjs";
import { catchError, tap, map, retry, timeout } from "rxjs/operators";

import { RegisterResponseDto } from "../modules/admin/models/register.response.dto";
import { AutenticarResponseDto } from "../pages/models/control-app/autenticar.response.dto";
import { UsuarioResponseDto } from "../modules/admin/models/usuario.response.dto";
import { environment } from "../../environments/environment";


export interface DispositivoDto {
  id: string;
  dataAtualizacao: string;
  version?: string;
  buildNumber?: string;
  appName?: string;
  packageName?: string;
  marca?: string;
  modelo?: string;
  fabricante?: string;
  sistema?: string;
  ipLocal?: string;
  tipoConexao?: string | null;
  conexaoFormatada?: string | null;
  androidId?: string;
  uuidApp?: string;
  latitude?: string;
  longitude?: string;
  nivelBateria?: number | null;
  estaCarregando?: boolean | null;
  usuarioId?: string;
  dispositivoId?: string;
}

export interface DispositivoHistoricoResponse {
  dispositivoId: string;
  marca?: string;
  modelo?: string;
  nomeUsuario?: string;
  historicos: DispositivoDto[];
}

@Injectable({
  providedIn: 'root'
})

export class ControllAppService {

  constructor(
    private httpClient: HttpClient,
    private router: Router
  ) { }
  private apiUrl = environment.controllApp;


  // Fix the headers method return type
  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    if (!token) {
      return new HttpHeaders();
    }
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  register(formData: FormData): Observable<RegisterResponseDto> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const url = `${environment.controllApp}/usuario/register`;
    formData.forEach((value, key) => {
      console.log(`FormData - ${key}: ${value}`);
    });

    return this.httpClient.post<RegisterResponseDto>(url, formData, {
      headers,
      reportProgress: true
    }).pipe(
      tap(response => {
        Swal.fire({
          title: 'Sucesso',
          text: 'Usuário cadastrado com sucesso!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      }),
      catchError(this.handleError)
    );
  }

  autenticar(userName: string, senha: string): Observable<any> {
    // Primeira requisição para o endpoint 1
    const request1 = this.httpClient.post<AutenticarResponseDto>(
      environment.controllApp + '/usuario/authenticate',
      { userName, senha }
    );

    // Executa ambas as requisições simultaneamente usando forkJoin
    return forkJoin([request1]);
  }

  private handleError(error: HttpErrorResponse) {

    
    if (error.status === 403) {
      
      // Tenta extrair a mensagem de várias formas possíveis
      let mensagem = 'Você não tem permissão para realizar esta operação.';
      
      // Personalizações de mensagens por URL
      if (error.url?.includes('/usuario/register')) {
        mensagem = 'Somente SuperAdministrador pode cadastrar.';
      } 
      else if (error.url?.includes('/usuario/delete')) {
        mensagem = 'Somente SuperAdministrador pode excluir.';
      }
      else if (typeof error.error === 'string') {
        mensagem = error.error;
      } 
      else if (error.error?.message) {
        mensagem = error.error.message;
      }
      
      // Mostrar o erro com SweetAlert diretamente aqui
      Swal.fire({
        title: 'Permissão Negada',
        text: mensagem,
        icon: 'error',
        confirmButtonText: 'OK'
      });
      
      return throwError(() => new Error(mensagem));
    }

    if (error.status === 401) {
      return throwError(() => new Error('Não autorizado. Por favor, faça login novamente.'));
    }
    if (error.status === 0) {
      return throwError(() => new Error('Erro de conexão com o servidor. Verifique sua conexão.'));
    }
    
    // Add specific handling for 400 Bad Request errors
    if (error.status === 400) {

      let mensagem = 'Erro na requisição.';
      
      if (typeof error.error === 'string') {
        // API might return the error as a plain string
        mensagem = error.error;
        
        // Check for specific error cases in the message
        if (mensagem.toLowerCase().includes('cpf')) {
          mensagem = 'CPF já cadastrado.';
        } else if (mensagem.toLowerCase().includes('username')) {
          mensagem = 'Nome de usuário já cadastrado.';
        } else if (mensagem.toLowerCase().includes('email')) {
          mensagem = 'Email já cadastrado.';
        }
      } else if (error.error?.message) {
        // Or as a structured object with a message property
        mensagem = error.error.message;
      } else if (error.error?.errors) {
        // Or as validation errors object
        const errorsObj = error.error.errors;
        const firstErrorKey = Object.keys(errorsObj)[0];
        if (firstErrorKey) {
          mensagem = errorsObj[firstErrorKey][0] || mensagem;
        }
      }
      
      // Show the error with SweetAlert
      Swal.fire({
        title: 'Erro no Cadastro',
        text: mensagem,
        icon: 'error',
        confirmButtonText: 'OK'
      });
      
      return throwError(() => new Error(mensagem));
    }
    
    // Always propagate the original error to ensure the interceptor gets it
    let message = error.error?.message || error.message || 'Erro desconhecido';
    
    // For unhandled error statuses, show a SweetAlert with the error message
    Swal.fire({
      title: 'Erro',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
    
    return throwError(() => new Error(message));
  }

  uploadFile(file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.post<{ url: string }>('https://localhost:5141/api/image/upload', formData);
  }


  atualizarStatusUsuario(usuarioId: string, isOnline: boolean): Observable<any> {
    const formData = new FormData();
    formData.append('isOnline', JSON.stringify(isOnline));

    return this.httpClient.put(`${this.apiUrl}/usuario/update/${usuarioId}`, formData);
  }

  atualizarCoordenadasUsuario(usuarioId: string, latitude: string, longitude: string): Observable<any> {
    const token = localStorage.getItem('token');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token || ''}`
    });

    const url = `${this.apiUrl}/usuario/update-location/${usuarioId}?latitude=${latitude}&longitude=${longitude}`;

    return this.httpClient.put(
      url,
      {},
      {
        headers,
        responseType: 'text' as const
      }
    ).pipe(
      catchError((error) => {
        if (error.status === 440) {
          Swal.fire({
            title: 'Sessão Expirada',
            text: 'Sua conta foi acessada em outro dispositivo. Por favor, faça login novamente.',
            icon: 'warning',
            confirmButtonText: 'OK'
          }).then((result) => {
            if (result.isConfirmed) {
              localStorage.clear();
              this.router.navigate(['/pages/usuarios/autenticar']);
            }
          });
        }
        return throwError(() => error);
      })
    );
  }

  atualizarUsuario(usuarioId: string, formData: FormData): Observable<RegisterResponseDto> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token || ''}`
    });

    return this.httpClient.put<RegisterResponseDto>(
      `${this.apiUrl}/usuario/update/${usuarioId}`,
      formData,
      { headers }
    ).pipe(
      tap(response => {
        Swal.fire({
          title: 'Sucesso',
          text: 'Usuário atualizado com sucesso!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      }),
      catchError(this.handleError)
    );
  }

  usuarioGetById(id: string): Observable<UsuarioResponseDto> {
    return this.httpClient.get<UsuarioResponseDto>(`${this.apiUrl}/usuario/${id}`);
  }

  deleteById(id: string): Observable<any> {
    // Restaurar o método DELETE original para que o servidor possa retornar 403 Forbidden
    return this.httpClient.delete(`${this.apiUrl}/usuario/delete/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error);
      })
    );
  }

  atualizarEmpresa(empresaId: string, data: any): Observable<any> {
    return this.httpClient.put<any>(`${this.apiUrl}/usuario/empresa/${empresaId}`, data, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }
  cadastrarEmpresa(data: any): Observable<any> {
    return this.httpClient.post<any>(`${this.apiUrl}/usuario/empresa`, data, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  buscarEmpresasPorId(empresaId: string): Observable<any> {
    return this.httpClient.get<any>(`${this.apiUrl}/usuario/empresa/${empresaId}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  buscarEmpresas(): Observable<any> {
    return this.httpClient.get<any>(`${this.apiUrl}/usuario/empresa`, {
      headers: this.getHeaders()
    }).pipe(
      tap(response => {
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  deletarEmpresa(empresaId: string): Observable<any> {
    return this.httpClient.delete<any>(`${this.apiUrl}/usuario/empresa/${empresaId}`, {
      headers: this.getHeaders(),
      responseType: 'text' as 'json' // Add this line to handle empty responses
    }).pipe(
      map(() => ({ success: true })), // Transform empty response to a success object
      catchError(this.handleError)
    );
  }

  roteiroCoordenadasUsuario(usuarioId: string, latitude: string, longitude: string): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token || ''}`,
      'Content-Type': 'application/json'
    });

    const url = `${this.apiUrl}/usuario/registrar-localizacao/${usuarioId}?latitude=${latitude}&longitude=${longitude}`;

    return this.httpClient.post(url, null, {
      headers,
      responseType: 'text' as const
    });
  }

  buscarDispositivosPorUsuarioId(usuarioId: string): Observable<DispositivoDto[]> {
    const url = `${this.apiUrl}/dispositivo/${usuarioId}`;
    return this.httpClient.get<DispositivoDto[]>(url, {
      headers: this.getHeaders()
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        // If the endpoint returns 404, treat it as "no devices" (not an application error)
        if (error && error.status === 404) {
          // Return an empty array so callers can handle "no devices" silently
          return of([] as DispositivoDto[]);
        }
        // For other errors, keep the existing behavior (which shows alerts)
        return this.handleError(error);
      })
    );
  }

  /**
   * Busca o histórico completo de dispositivos para o usuário informado.
   * Endpoint: GET /controllapp/dispositivo/{usuarioId}/historico
   */
  buscarHistoricoDispositivoPorUsuarioId(usuarioId: string): Observable<DispositivoHistoricoResponse> {
    const url = `${this.apiUrl}/dispositivo/${usuarioId}/historico`;
    return this.httpClient.get<DispositivoHistoricoResponse>(url, {
      headers: this.getHeaders()
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        // If the resource is not found, return a default empty structure instead of throwing
        if (error && error.status === 404) {
          const empty: DispositivoHistoricoResponse = {
            dispositivoId: '',
            marca: undefined,
            modelo: undefined,
            nomeUsuario: undefined,
            historicos: []
          };
          return of(empty);
        }
        return this.handleError(error);
      })
    );
  }

}

